import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { LineSample } from './drawerControls'
import { QUICK_BAR_OPACITIES, opacityToPercent, percentToOpacity } from './quickBarUtils'
import { Z_MODAL_POPPER } from './zLayers'

/* ------------------------------------------------------------------ */
/*  Consolidated line-style control                                     */
/* ------------------------------------------------------------------ */

type LineStyle = 'solid' | 'dashed' | 'dotted'

const STYLE_LABELS: Record<LineStyle, string> = {
  solid: 'Solid',
  dashed: 'Dashed',
  dotted: 'Dotted',
}

/**
 * One control for a line's whole look: a preview trigger that opens a popover
 * holding **Weight**, **Style** and **Transparency** together. The single editor
 * both the drawing quick bar and the settings drawer use, so a line is styled in
 * one place instead of three scattered controls.
 *
 * Emits partial patches (`{ size?, style?, opacity? }`); the caller maps
 * `opacity` (0..1) onto its own style field (overlays use `fillOpacity`). The
 * popover portals to `<body>` so a scroll/overflow ancestor can't clip it, and
 * carries `data-chart-chrome` + `data-line-style-popover` so neither the chart's
 * outside-click deselect nor the drawer's outside-click dismiss fires while the
 * user is picking inside it.
 */
export function LineStyleControl({
  size,
  style,
  opacity,
  styleOptions = ['solid', 'dashed', 'dotted'],
  widthOptions = [1, 2, 3, 4, 5],
  onChange,
  triggerClassName,
  title = 'Line style',
}: {
  size: number
  style: LineStyle
  /** Stored opacity, 0..1 (undefined-safe: treat missing as fully opaque). */
  opacity: number | undefined
  styleOptions?: LineStyle[]
  widthOptions?: number[]
  onChange: (patch: { size?: number; style?: LineStyle; opacity?: number }) => void
  /** Overrides the trigger box so the bar and the drawer can size it to fit. */
  triggerClassName?: string
  title?: string
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)

  useLayoutEffect(() => {
    if (!open) return
    const el = triggerRef.current
    if (!el) return
    const measure = () => setRect(el.getBoundingClientRect())
    measure()
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [open])

  // Close on an outside click (the popover is portaled, so guard by ref/attr).
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null
      if (triggerRef.current?.contains(t) || popRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown, true)
    return () => document.removeEventListener('mousedown', onDown, true)
  }, [open])

  const pct = opacityToPercent(opacity)

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={title}
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={
          triggerClassName ??
          'flex h-7 w-24 items-center justify-between gap-1 rounded border border-border-strong bg-field px-2 text-[hsl(var(--chart-text))] outline-hidden focus:border-primary'
        }
      >
        <LineSample size={size} style={style} opacity={opacity ?? 1} width={28} />
        <svg
          width={10}
          height={10}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="shrink-0 opacity-60"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            ref={popRef}
            data-line-style-popover=""
            data-chart-popup=""
            data-chart-chrome=""
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: rect.bottom + 4,
              left: rect.left,
              zIndex: Z_MODAL_POPPER,
              minWidth: Math.max(rect.width, 180),
            }}
            className="flex flex-col gap-2 rounded-md border border-primary/15 bg-popover p-2 text-[hsl(var(--chart-text))] shadow-lg shadow-black/30"
          >
            <Group label="Weight">
              {widthOptions.map((w) => (
                <SampleButton
                  key={w}
                  label={`${w}px`}
                  selected={w === size}
                  onClick={() => onChange({ size: w })}
                >
                  <LineSample size={w} style={style} width={28} />
                </SampleButton>
              ))}
            </Group>
            <Group label="Style">
              {styleOptions.map((s) => (
                <SampleButton
                  key={s}
                  label={STYLE_LABELS[s]}
                  selected={s === style}
                  onClick={() => onChange({ style: s })}
                >
                  <LineSample size={size} style={s} width={28} />
                </SampleButton>
              ))}
            </Group>
            <Group label="Transparency">
              {QUICK_BAR_OPACITIES.map((p) => (
                <TextButton
                  key={p}
                  label={`${p}%`}
                  selected={p === pct}
                  onClick={() => onChange({ opacity: percentToOpacity(p) })}
                >
                  {p}%
                </TextButton>
              ))}
            </Group>
          </div>,
          document.body,
        )}
    </>
  )
}

function Group({ label, children }: { label: string; children: ReactNode }) {
  // Label stacked ABOVE its options (not beside) so the popover stays narrow.
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-1">{children}</div>
    </div>
  )
}

function SampleButton({
  label,
  selected,
  onClick,
  children,
}: {
  label: string
  selected: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={selected}
      onClick={onClick}
      className={
        'flex h-6 w-10 items-center justify-center rounded-sm border transition-colors ' +
        (selected ? 'border-primary bg-primary/15' : 'border-transparent hover:bg-accent')
      }
    >
      {children}
    </button>
  )
}

function TextButton({
  label,
  selected,
  onClick,
  children,
}: {
  label: string
  selected: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={selected}
      onClick={onClick}
      className={
        'rounded-sm px-1.5 py-0.5 text-[11px] transition-colors ' +
        (selected
          ? 'bg-primary/15 text-primary'
          : 'text-[hsl(var(--chart-text)/0.8)] hover:bg-accent')
      }
    >
      {children}
    </button>
  )
}
